import { cn } from '@attive/ui/cn'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { type Options as HotkeyOptions, useHotkeys } from 'react-hotkeys-hook'
import { Kbd } from './kbd'

const buttonVariants = cva(
	'inline-flex items-center justify-center rounded-md font-outfit text-button-text outline-none ring-offset-background transition-all duration-200 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default:
					'border border-border/40 bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover hover:shadow-[0px_2px_8px_0px_rgba(11,11,11,0.2)]',
				destructive:
					'rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive-hover',
				outline:
					'border border-border/80 bg-background/50 text-foreground shadow-sm backdrop-blur-sm hover:bg-accent hover:border-border hover:shadow-md dark:border-border/50 dark:bg-background/30 dark:hover:bg-accent/80 dark:hover:border-border/70',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
				ghost:
					'bg-transparent text-foreground-subtle hover:bg-secondary-hover rounded-md',
				link: 'bg-transparent text-foreground-subtle hover:bg-transparent rounded-md',
				gradient:
					'rounded-lg bg-gradient-to-b from-[rgba(0,102,106,1)] to-[rgba(0,76,88,1)] text-white hover:bg-gradient-to-b hover:from-[rgba(0,102,106,1)] hover:to-[rgba(0,76,88,1)] hover:bg-[rgba(255,255,255,0.1)]',
				'accent-dark':
					'bg-[hsl(180_2%_7%)] text-primary hover:bg-[hsl(180_2%_10%)] shadow-sm dark:bg-[hsl(180_4%_14%)] dark:hover:bg-[hsl(180_5%_18%)]',
			},
			size: {
				default: 'h-10 px-4 py-2',
				wide: 'px-24 py-5',
				sm: 'h-9 rounded-md px-3',
				xs: 'h-8 rounded-md px-2 text-xs',
				lg: 'h-11 rounded-md px-8',
				pill: 'rounded-full px-12 py-3 leading-3',
				icon: 'h-10 w-10',
			},
			focusRing: {
				true: 'ring-ring ring-offset-2 focus-within:ring-2 focus-visible:ring-2',
				false:
					'focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
			focusRing: true,
		},
	},
)

interface ShortcutConfig {
	keys: string
	options?: HotkeyOptions
	showKbd?: boolean
	displayKeys?: string // Optional: custom display text for the kbd
}

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
	noFocusRing?: boolean
	shortcut?: ShortcutConfig
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			focusRing,
			asChild = false,
			noFocusRing = false,
			shortcut,
			children,
			onClick,
			disabled,
			...props
		},
		ref,
	) => {
		// Set up keyboard shortcut if provided
		useHotkeys(
			shortcut?.keys ?? '',
			(e: KeyboardEvent) => {
				e.preventDefault()
				if (!disabled && onClick) {
					// Create a synthetic click event
					const syntheticEvent = new MouseEvent('click', {
						bubbles: true,
						cancelable: true,
					}) as unknown as React.MouseEvent<HTMLButtonElement>
					onClick(syntheticEvent)
				}
			},
			{
				enabled: !!shortcut && !disabled,
				...shortcut?.options,
			},
			[onClick, disabled],
		)

		const Comp = asChild ? Slot : 'button'

		// Determine if we should show the kbd indicator
		const showKbd = shortcut && shortcut.showKbd !== false

		return (
			<Comp
				className={cn(
					buttonVariants({ variant, size, focusRing: !noFocusRing, className }),
				)}
				ref={ref}
				onClick={onClick}
				disabled={disabled}
				{...props}
			>
				{showKbd ? (
					<>
						{children}
						<Kbd inline className="ml-2">
							{shortcut.displayKeys || shortcut.keys}
						</Kbd>
					</>
				) : (
					children
				)}
			</Comp>
		)
	},
)
Button.displayName = 'Button'

export { Button, buttonVariants }
